import { useEffect } from "react";
import { DEFAULT_OG_IMAGE, absoluteUrl, type JsonLd } from "../seo/site";

export interface SeoOptions {
  title: string;
  description: string;
  /** Site-relative path; the canonical is always on the production origin. */
  path: string;
  image?: string;
  type?: "website" | "article";
  robots?: string;
  jsonLd?: JsonLd[];
}

const MANAGED_ATTR = "data-seo-managed";

function upsert(selector: string, create: () => HTMLElement, apply: (el: HTMLElement) => void) {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  apply(el);
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  upsert(
    `meta[${attr}="${key}"]`,
    () => {
      const m = document.createElement("meta");
      m.setAttribute(attr, key);
      return m;
    },
    (m) => m.setAttribute("content", content),
  );
}

/**
 * Sets the page's title, description, canonical, social tags and JSON-LD, and puts the
 * previous values back on unmount. The pre-rendered HTML for public routes carries the
 * same values, so what the crawler reads first and what it renders agree.
 */
export default function useSeo(options: SeoOptions | null) {
  const key = options ? JSON.stringify(options) : "";
  useEffect(() => {
    if (!options) return;
    const canonical = absoluteUrl(options.path);
    const image = options.image ? absoluteUrl(options.image) : DEFAULT_OG_IMAGE;

    const snapshot = {
      title: document.title,
      metas: Array.from(document.head.querySelectorAll("meta[name], meta[property]")).map((m) => ({
        el: m,
        content: m.getAttribute("content"),
      })),
      canonical: document.head.querySelector('link[rel="canonical"]')?.getAttribute("href") ?? null,
    };
    const created: Element[] = [];
    const before = new Set(Array.from(document.head.children));

    document.title = options.title;
    setMeta("name", "description", options.description);
    setMeta("name", "robots", options.robots ?? "index, follow, max-image-preview:large");
    setMeta("property", "og:type", options.type ?? "website");
    setMeta("property", "og:title", options.title);
    setMeta("property", "og:description", options.description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", image);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", options.title);
    setMeta("name", "twitter:description", options.description);
    setMeta("name", "twitter:image", image);
    upsert(
      'link[rel="canonical"]',
      () => {
        const l = document.createElement("link");
        l.setAttribute("rel", "canonical");
        return l;
      },
      (l) => l.setAttribute("href", canonical),
    );

    // Pre-rendered route JSON-LD is replaced, not duplicated, once the app takes over.
    document.head.querySelectorAll(`script[${MANAGED_ATTR}]`).forEach((s) => s.remove());
    for (const doc of options.jsonLd ?? []) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute(MANAGED_ATTR, "");
      s.textContent = JSON.stringify(doc);
      document.head.appendChild(s);
    }
    Array.from(document.head.children).forEach((el) => {
      if (!before.has(el)) created.push(el);
    });

    return () => {
      document.title = snapshot.title;
      snapshot.metas.forEach(({ el, content }) => {
        if (content === null) el.removeAttribute("content");
        else el.setAttribute("content", content);
      });
      const link = document.head.querySelector('link[rel="canonical"]');
      if (link && snapshot.canonical !== null) link.setAttribute("href", snapshot.canonical);
      created.forEach((el) => el.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
